import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import valueIcon from 'assets/images/icons/SprengnetterAppsValue.png'
import Button from 'components/Button/Button'
import { StyledContainer } from '../../ProductCheckout/ProductCheckout.styled'
import { useTranslation } from 'react-i18next'
import { selectCustomerConfiguration } from 'slices/customerConfigurationSlice'
import { selectCustomerCoins, selectProductsCost } from 'slices/coinsSlice'
import { useLazyGetCustomerCoinsQuery } from 'services/coins-api'
import NoCoinsContainer from '../../ProductCheckout/NoCoinsContainer/NoCoinsContainer'
import { selectAddressFormData } from 'slices/addressFormSlice'
import { useToastEffect } from 'components/Toast/hooks/useToastEffect'
import ProductGenerationError from 'components/ProductGenerationError/ProductGenerationError'
import ProductGenerationSuccess from 'components/ProductGenerationSuccess/ProductGenerationSuccess'
import { findInArray, isProductBilled } from 'common/utils/generic'
import { useLazyCreateValueQuery, useLazyGetValueDocumentQuery } from 'services/value-api'
import {
  StyledButtonsContainer,
  StyledResultContainer,
  StyledResultLabel,
  StyledResultRow,
} from './ValueCheckout.styled'
import moment from 'moment'

export interface ValueCheckoutProps {
  isReportGenerated: boolean
  setReportGenerated: Dispatch<SetStateAction<boolean>>
  onBackdropClick: () => void
}

const ValueCheckout = (props: ValueCheckoutProps) => {
  const { isReportGenerated, setReportGenerated, onBackdropClick } = props
  const { t } = useTranslation()
  const addressFormData = useSelector(selectAddressFormData)
  const servicePrices = useSelector(selectProductsCost)
  const customerConfig = useSelector(selectCustomerConfiguration)
  const customerCoins = useSelector(selectCustomerCoins)
  const premiumCoins =
    customerCoins.coins?.accounts?.find((item) => item.type === 'on-demand')?.amount || 0
  const subscriptionCoinsData = customerCoins.coins?.accounts?.find(
    (item) => item.type === 'subscription'
  )
  const immoPointsEnabled = customerConfig?.immopointsEnabled || false
  const immoPoints = customerCoins.coins?.immoPoints || 0
  const subscriptionCoins = subscriptionCoinsData?.amount
  const valueProduct = findInArray(customerConfig?.products, 'name', 'value')
  const isValueBilled = isProductBilled(valueProduct, customerConfig)
  const [showToastError, setShowToastError] = useState(false)
  const [valuationId, setValuationId] = useState('')
  const dateFormat = 'DD.MM.YYYY'

  const hasEnoughImmoPoints = immoPoints && servicePrices?.value <= immoPoints
  const hasEnoughCoins = () => {
    let valuePrice = servicePrices?.value
    if (immoPointsEnabled) {
      valuePrice = 180
    }
    return (
      (premiumCoins || subscriptionCoins) &&
      (valuePrice <= subscriptionCoins || valuePrice <= premiumCoins)
    )
  }

  const [getCustomerCoins] = useLazyGetCustomerCoinsQuery()

  const [createValue, { data, isFetching, isSuccess, isError }] = useLazyCreateValueQuery()
  const [
    getValueDocument,
    {
      data: documentData,
      isFetching: isDocumentFetching,
      isSuccess: isDocumentSuccess,
      isError: isDocumentError,
    },
  ] = useLazyGetValueDocumentQuery()

  const handleCreateValueButtonClick = () => {
    if (isFetching) return
    createValue({ id: addressFormData.addressData.id })
  }

  const handleOpenValueButtonClick = () => {
    window.open(valueProduct.url + `?auftrag_id=${valuationId}`, '_blank')
  }

  const handleDownloadValueDocument = () => {
    if (isDocumentFetching) return
    getValueDocument({ id: addressFormData.addressData.id })
  }

  useEffect(() => {
    if (isDocumentSuccess) {
      const anchor = document.createElement('a')
      const fileName = `value_${addressFormData.addressData.zip}-${addressFormData.addressData.town}`
      anchor.setAttribute('download', fileName)
      document.body.appendChild(anchor)
      const objectUrl = window.URL.createObjectURL(documentData)
      anchor.href = objectUrl
      anchor.click()

      window.URL.revokeObjectURL(objectUrl)
      document.body.removeChild(anchor)
    }
  }, [isDocumentSuccess, documentData, addressFormData])

  useEffect(() => {
    if (isSuccess) {
      getCustomerCoins()
      setShowToastError(false)
      setValuationId(data.valuationId)
    }
    if (isSuccess || isError) {
      setReportGenerated(true)
    }
  }, [data, isSuccess, isError, getCustomerCoins, setReportGenerated])

  useToastEffect(t('newAddressModal.finishTab.valueInfo.error.toast'), 'ERROR', [
    isError,
    showToastError,
  ])

  useToastEffect(t('newAddressModal.finishTab.valueInfo.document.error.toast'), 'ERROR', [
    isDocumentError,
    true,
  ])

  return (
    <>
      {!isReportGenerated &&
        !immoPointsEnabled &&
        (!isValueBilled || hasEnoughCoins() ? (
          <StyledContainer>
            <img src={valueIcon} alt="" className="app-icon" />
            <h1>{t('common.generateValue')}</h1>
            {isValueBilled && (
              <p>
                {t('newAddressModal.finishTab.valueInfo.description', {
                  value: servicePrices?.value,
                })}
              </p>
            )}
            <div onClick={() => handleCreateValueButtonClick()}>
              <Button kind="primary" loading={isFetching} disabled={isFetching}>
                {isFetching ? t('common.loadingButton') : t('common.generateValue')}
              </Button>
            </div>
          </StyledContainer>
        ) : (
          <NoCoinsContainer
            icon={valueIcon}
            descriptionKey="newAddressModal.finishTab.valueInfo.noCoinsDescription"
            buttonLabel={t('common.generateValue')}
            price={servicePrices?.value}
          />
        ))}
      {!isReportGenerated &&
        immoPointsEnabled &&
        (!isValueBilled || hasEnoughCoins() || hasEnoughImmoPoints ? (
          <StyledContainer>
            <img src={valueIcon} alt="" className="app-icon" />
            <h1>{t('common.generateValue')}</h1>
            {isValueBilled && hasEnoughCoins() ? (
              <p>{t(`newAddressModal.finishTab.valueInfo.freeValue.description`)}</p>
            ) : (
              <></>
            )}
            {isValueBilled && !hasEnoughCoins() && (
              <p>
                {t(`newAddressModal.finishTab.valueInfo.immoPoints.description`, {
                  value: servicePrices?.value,
                })}
              </p>
            )}
            <div onClick={() => handleCreateValueButtonClick()}>
              <Button kind="primary" loading={isFetching} disabled={isFetching}>
                {isFetching ? t('common.loadingButton') : t('common.generateValue')}
              </Button>
            </div>
          </StyledContainer>
        ) : (
          <NoCoinsContainer
            icon={valueIcon}
            descriptionKey="newAddressModal.finishTab.valueInfo.noImmoPointsDescription"
            buttonLabel={t('common.generateValue')}
            price={servicePrices?.value}
          />
        ))}
      {isSuccess && (
        <ProductGenerationSuccess
          icon={valueIcon}
          title={t('newAddressModal.finishTab.valueInfo.downloadTitle')}
        >
          <StyledResultLabel>{t('newAddressModal.finishTab.valueInfo.result')}</StyledResultLabel>
          <StyledResultContainer>
            <StyledResultRow>
              <div>{t('newAddressModal.finishTab.valueInfo.marketValue')}</div>
              <div>
                {data.value
                  ? `${data.value
                      .toFixed(2)
                      .replace('.', ',')
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} €`
                  : '—'}
              </div>
            </StyledResultRow>
            <StyledResultRow>
              <div>{t('newAddressModal.finishTab.valueInfo.marketValuePerMeterSquare')}</div>
              <div>
                {data.valuePerMeterSquare
                  ? `${data.valuePerMeterSquare
                      .toFixed(2)
                      .replace('.', ',')
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} €`
                  : '—'}
              </div>
            </StyledResultRow>
            {data.grossProfitMultiplier && (
              <StyledResultRow>
                <div>{t('newAddressModal.finishTab.valueInfo.grossProfitMultiplier')}</div>
                <div>
                  {data.grossProfitMultiplier
                    .toFixed(2)
                    .replace('.', ',')
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                </div>
              </StyledResultRow>
            )}
            <StyledResultRow>
              <div>{t('newAddressModal.valuationDate')}</div>
              <div>{moment(data.valuationDate).format(dateFormat)}</div>
            </StyledResultRow>
          </StyledResultContainer>
          <p>{t('newAddressModal.finishTab.valueInfo.downloadDescription')}</p>
          <StyledButtonsContainer>
            <Button kind="primary" onClick={handleOpenValueButtonClick}>
              {t('common.showValueButton')}
            </Button>
            <Button
              kind="primary"
              loading={isDocumentFetching}
              disabled={isDocumentFetching}
              onClick={handleDownloadValueDocument}
            >
              {isDocumentFetching ? t('common.loadingButton') : t('common.downloadDocx')}
            </Button>
            <Button kind="secondary" onClick={onBackdropClick}>
              {t('common.closeButton')}
            </Button>
          </StyledButtonsContainer>
        </ProductGenerationSuccess>
      )}
      {(isError || showToastError) && (
        <ProductGenerationError
          icon={valueIcon}
          title={t('newAddressModal.finishTab.valueInfo.errorTitle')}
          message1={t('newAddressModal.finishTab.valueInfo.errorDescription.1')}
          message2={t('newAddressModal.finishTab.valueInfo.errorDescription.2')}
          isLoading={isFetching}
          tryAgainAction={() => {
            setShowToastError(true)
            handleCreateValueButtonClick()
          }}
        />
      )}
    </>
  )
}

export default ValueCheckout
